import React, { useState, useRef, useEffect } from 'react';
import { Filter, X, ChevronDown } from 'lucide-react';

export interface StatsFilters {
  gender?: 'all' | 'male' | 'female';
  ageRange?: 'all' | 'children' | 'youth' | 'adults' | 'seniors';
  hasComplementaryData?: 'all' | 'yes' | 'no';
  baptized?: 'all' | 'yes' | 'no';
  inMinistry?: 'all' | 'yes' | 'no';
  civilStatus?: 'all' | 'single' | 'married' | 'other';
}

interface StatsFiltersProps {
  filters: StatsFilters;
  onChange: (filters: StatsFilters) => void;
  totalResults?: number;
  isLoading?: boolean;
}

interface FilterOption {
  value: string;
  label: string;
}

interface FilterConfig {
  key: keyof StatsFilters;
  label: string;
  options: FilterOption[];
}

// Componente Dropdown Custom
interface CustomDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: FilterOption[];
  label: string;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  value,
  onChange,
  options,
  label
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const selectedLabel = options.find(o => o.value === value)?.label || options[0]?.label || 'Seleccionar';

  // Cerrar al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">
        {label}
      </label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 pr-10 
                   text-xs font-medium text-gray-700 text-left
                   hover:border-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500/20 
                   focus:border-purple-500 transition-all cursor-pointer flex items-center justify-between"
      >
        <span className="text-gray-900 truncate">{selectedLabel}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform absolute right-3 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
          {options.map((option, index) => (
            <div
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={`px-3 py-2.5 text-xs font-medium cursor-pointer transition-colors
                ${value === option.value 
                  ? 'bg-purple-50 text-purple-700' 
                  : 'text-gray-700 hover:bg-purple-50 hover:text-purple-700'
                }
                ${index === 0 ? 'first:rounded-t-lg' : ''}
                ${index === options.length - 1 ? 'last:rounded-b-lg' : ''}
              `}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const StatsFilters: React.FC<StatsFiltersProps> = ({
  filters,
  onChange,
  totalResults,
  isLoading
}) => {
  const hasActiveFilters = 
    filters.gender !== 'all' ||
    filters.ageRange !== 'all' ||
    filters.hasComplementaryData !== 'all' ||
    filters.baptized !== 'all' ||
    filters.inMinistry !== 'all' ||
    filters.civilStatus !== 'all';

  const handleReset = () => {
    onChange({
      gender: 'all',
      ageRange: 'all',
      hasComplementaryData: 'all',
      baptized: 'all',
      inMinistry: 'all',
      civilStatus: 'all'
    });
  };

  const updateFilter = <K extends keyof StatsFilters>(key: K, value: StatsFilters[K]) => {
    onChange({ ...filters, [key]: value });
  };

  const filterConfigs: FilterConfig[] = [
    {
      key: 'gender',
      label: 'Género',
      options: [
        { value: 'all', label: 'Todos' },
        { value: 'male', label: 'Hombres' },
        { value: 'female', label: 'Mujeres' }
      ]
    },
    {
      key: 'ageRange',
      label: 'Rango de Edad',
      options: [
        { value: 'all', label: 'Todas las edades' },
        { value: 'children', label: 'Niños (0-12)' },
        { value: 'youth', label: 'Jóvenes (13-25)' },
        { value: 'adults', label: 'Adultos (26-50)' },
        { value: 'seniors', label: 'Adultos Mayores (51+)' }
      ]
    },
    {
      key: 'hasComplementaryData',
      label: 'Datos Complementarios',
      options: [
        { value: 'all', label: 'Todos' },
        { value: 'yes', label: 'Con datos complementarios' },
        { value: 'no', label: 'Sin datos complementarios' }
      ]
    },
    {
      key: 'baptized',
      label: 'Estado de Bautismo',
      options: [
        { value: 'all', label: 'Todos' },
        { value: 'yes', label: 'Bautizados' },
        { value: 'no', label: 'No bautizados' }
      ]
    },
    {
      key: 'inMinistry',
      label: 'Ministerio',
      options: [
        { value: 'all', label: 'Todos' },
        { value: 'yes', label: 'En ministerio' },
        { value: 'no', label: 'No en ministerio' }
      ]
    },
    {
      key: 'civilStatus',
      label: 'Estado Civil',
      options: [
        { value: 'all', label: 'Todos' },
        { value: 'single', label: 'Soltero/a' },
        { value: 'married', label: 'Casado/a' },
        { value: 'other', label: 'Otros' }
      ]
    }
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center space-x-3">
          <div className="bg-purple-100 p-2 rounded-lg">
            <Filter className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="text-xs font-medium text-gray-900">Filtros de Segmentación</h3>
            <p className="text-xs text-gray-500">Filtra los datos del dashboard</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {hasActiveFilters && (
            <button
              onClick={handleReset}
              className="group flex items-center space-x-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-all duration-200"
            >
              <X className="w-4 h-4 group-hover:rotate-90 transition-transform duration-200" />
              <span>Limpiar</span>
            </button>
          )}
          
          {totalResults !== undefined && (
            <div className="bg-purple-50 px-4 py-2 rounded-lg border border-purple-100">
              <span className="text-xs text-gray-600">Resultados: </span>
              <span className="text-xs font-medium text-purple-700">
                {isLoading ? '...' : totalResults.toLocaleString()}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Filtros como dropdowns custom */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {filterConfigs.map((config) => (
          <CustomDropdown
            key={config.key}
            label={config.label}
            value={filters[config.key] || 'all'}
            onChange={(value) => updateFilter(config.key, value as any)}
            options={config.options}
          />
        ))}
      </div>

      {/* Chips de filtros activos */}
      {hasActiveFilters && (
        <div className="mt-5 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 mb-2">Filtros activos:</p>
          <div className="flex flex-wrap gap-2">
            {filterConfigs.map((config) => {
              const currentValue = filters[config.key];
              if (currentValue === 'all') return null;
              
              const option = config.options.find(o => o.value === currentValue);
              if (!option) return null;
              
              return (
                <span
                  key={config.key}
                  className="inline-flex items-center space-x-1 px-2.5 py-1 text-xs font-medium bg-purple-100 text-purple-700 rounded-full"
                >
                  <span>{config.label}:</span>
                  <span className="font-medium">{option.label}</span>
                  <button
                    onClick={() => updateFilter(config.key, 'all')}
                    className="ml-1 p-0.5 hover:bg-purple-200 rounded-full transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default StatsFilters;
